// Utilitaires pour l'export du planning
import { formatDuree } from './formatDuree';

export const exporterPlanningPDF = (cartes, semaine, cartesParProduit) => {
  // Créer le contenu HTML pour impression
  const html = genererHTMLPlanning(cartes, semaine, cartesParProduit);
  
  // Ouvrir dans une nouvelle fenêtre pour impression
  const printWindow = window.open('', '_blank');
  printWindow.document.write(html);
  printWindow.document.close();
  
  // Attendre le chargement puis lancer l'impression
  printWindow.onload = () => {
    printWindow.print();
  };
};

export const exporterPlanningExcel = (cartes, semaine) => {
  // Générer un CSV (compatible Excel)
  const csv = genererCSVPlanning(cartes, semaine);
  
  // Créer un lien de téléchargement
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `planning_semaine_${semaine}_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const genererHTMLPlanning = (cartes, semaine, cartesParProduit) => {
  const getWeekDates = (weekNumber) => {
    const year = new Date().getFullYear();
    const simple = new Date(year, 0, 1 + (weekNumber - 1) * 7);
    const dow = simple.getDay();
    const ISOweekStart = simple;
    if (dow <= 4) {
      ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
    } else {
      ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
    }
    const ISOweekEnd = new Date(ISOweekStart);
    ISOweekEnd.setDate(ISOweekStart.getDate() + 4);

    const formatDate = (date) => {
      return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    return `${formatDate(ISOweekStart)} - ${formatDate(ISOweekEnd)}`;
  };

  const calculerProgression = (carte) => {
    if (!carte.suivis || carte.suivis.length === 0) return { validees: 0, totales: 0, pourcentage: 0 };
    const validees = carte.suivis.filter(s => s.validee).length;
    return {
      validees,
      totales: carte.suivis.length,
      pourcentage: Math.round((validees / carte.suivis.length) * 100)
    };
  };

  const getBadgeLabel = (carte) => {
    const { validees, totales } = calculerProgression(carte);
    if (validees === totales && totales > 0) return '✓ Terminé';
    
    if (carte.date_expedition_max) {
      const aujourdhui = new Date();
      aujourdhui.setHours(0, 0, 0, 0);
      const dateFin = new Date(carte.date_expedition_max);
      dateFin.setHours(0, 0, 0, 0);
      const joursRestants = Math.ceil((dateFin - aujourdhui) / (1000 * 60 * 60 * 24));

      if (joursRestants < 0) return '⚠ En retard';
      if (joursRestants <= 2) return '⚡ Urgent';
    }
    
    return 'En cours';
  };

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Planning Semaine ${semaine}</title>
      <style>
        @page {
          size: A4 landscape;
          margin: 15mm;
        }
        
        body {
          font-family: Arial, sans-serif;
          margin: 0;
          padding: 20px;
          background: white;
        }
        
        .header {
          text-align: center;
          margin-bottom: 30px;
          border-bottom: 3px solid #1aabf9;
          padding-bottom: 15px;
        }
        
        .header h1 {
          margin: 0;
          color: #212529;
          font-size: 28px;
        }
        
        .header p {
          margin: 5px 0 0 0;
          color: #6c757d;
          font-size: 14px;
        }
        
        .stats {
          display: flex;
          justify-content: space-around;
          margin-bottom: 30px;
          padding: 15px;
          background: #f8f9fa;
          border-radius: 8px;
        }
        
        .stat-item {
          text-align: center;
        }
        
        .stat-value {
          font-size: 24px;
          font-weight: bold;
          color: #1aabf9;
        }
        
        .stat-label {
          font-size: 12px;
          color: #6c757d;
        }
        
        .kanban {
          display: grid;
          grid-template-columns: repeat(${Math.min(Object.keys(cartesParProduit).length, 3)}, 1fr);
          gap: 20px;
        }
        
        .colonne {
          break-inside: avoid;
        }
        
        .colonne-header {
          background: #1aabf9;
          color: white;
          padding: 12px;
          border-radius: 8px 8px 0 0;
          font-weight: bold;
          font-size: 16px;
        }
        
        .colonne-body {
          border: 2px solid #e0e5ea;
          border-top: none;
          border-radius: 0 0 8px 8px;
          padding: 15px;
          min-height: 100px;
        }
        
        .carte {
          background: white;
          border: 2px solid #e0e5ea;
          border-radius: 8px;
          padding: 12px;
          margin-bottom: 12px;
          page-break-inside: avoid;
        }
        
        .carte-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }
        
        .carte-code {
          font-family: 'Courier New', monospace;
          font-weight: bold;
          font-size: 14px;
        }
        
        .carte-badge {
          background: #f8f9fa;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: bold;
        }
        
        .carte-nom {
          color: #495057;
          font-size: 13px;
          margin-bottom: 8px;
        }
        
        .carte-info {
          font-size: 11px;
          color: #6c757d;
          display: flex;
          justify-content: space-between;
        }
        
        .progression-bar {
          width: 100%;
          height: 6px;
          background: #e0e5ea;
          border-radius: 3px;
          overflow: hidden;
          margin: 8px 0;
        }
        
        .progression-fill {
          height: 100%;
          background: #1aabf9;
        }
        
        .footer {
          margin-top: 30px;
          padding-top: 15px;
          border-top: 2px solid #e0e5ea;
          text-align: center;
          color: #6c757d;
          font-size: 11px;
        }
        
        @media print {
          body {
            padding: 0;
          }
          
          .no-print {
            display: none;
          }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>📅 Planning de Production - Semaine ${semaine}</h1>
        <p>${getWeekDates(semaine)}</p>
      </div>
      
      <div class="stats">
        <div class="stat-item">
          <div class="stat-value">${cartes.length}</div>
          <div class="stat-label">Cartes</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">${Object.keys(cartesParProduit).length}</div>
          <div class="stat-label">Produits</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">${formatDuree(cartes.reduce((sum, c) => {
            const ops = c.suivis?.filter(s => !s.validee) || [];
            return sum + ops.reduce((s, op) => s + (op.operations?.temps_realisation || 0), 0);
          }, 0))}</div>
          <div class="stat-label">Charge totale</div>
        </div>
      </div>
      
      <div class="kanban">
        ${Object.entries(cartesParProduit).map(([produitNom, cartesColonne]) => `
          <div class="colonne">
            <div class="colonne-header">
              ${produitNom} (${cartesColonne.length})
            </div>
            <div class="colonne-body">
              ${cartesColonne.map(carte => {
                const progression = calculerProgression(carte);
                return `
                  <div class="carte">
                    <div class="carte-header">
                      <div class="carte-code">${carte.code_barre}</div>
                      <div class="carte-badge">${getBadgeLabel(carte)}</div>
                    </div>
                    ${carte.nom_attribue ? `<div class="carte-nom">${carte.nom_attribue}</div>` : ''}
                    <div class="progression-bar">
                      <div class="progression-fill" style="width: ${progression.pourcentage}%"></div>
                    </div>
                    <div class="carte-info">
                      <span>${progression.validees}/${progression.totales} ops</span>
                      ${carte.date_expedition_max ? `<span>Exp: ${new Date(carte.date_expedition_max).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>` : ''}
                    </div>
                  </div>
                `;
              }).join('')}
            </div>
          </div>
        `).join('')}
      </div>
      
      <div class="footer">
        Document généré le ${new Date().toLocaleString('fr-FR')} • Atelier Production
      </div>
    </body>
    </html>
  `;
};

const genererCSVPlanning = (cartes, semaine) => {
  // En-têtes CSV
  const headers = [
    'Code Barre',
    'Produit',
    'Nom Attribué',
    'Date Expédition',
    'Semaine',
    'Statut',
    'Opérations Validées',
    'Opérations Totales',
    'Progression (%)',
    'Date Création'
  ];

  const calculerProgression = (carte) => {
    if (!carte.suivis || carte.suivis.length === 0) return { validees: 0, totales: 0, pourcentage: 0 };
    const validees = carte.suivis.filter(s => s.validee).length;
    return {
      validees,
      totales: carte.suivis.length,
      pourcentage: Math.round((validees / carte.suivis.length) * 100)
    };
  };

  const getStatutLabel = (carte) => {
    const { validees, totales } = calculerProgression(carte);
    if (validees === totales && totales > 0) return 'Terminé';
    
    if (carte.date_expedition_max) {
      const aujourdhui = new Date();
      aujourdhui.setHours(0, 0, 0, 0);
      const dateFin = new Date(carte.date_expedition_max);
      dateFin.setHours(0, 0, 0, 0);
      const joursRestants = Math.ceil((dateFin - aujourdhui) / (1000 * 60 * 60 * 24));

      if (joursRestants < 0) return 'En retard';
      if (joursRestants <= 2) return 'Urgent';
    }
    
    return 'En cours';
  };

  // Lignes de données
  const rows = cartes.map(carte => {
    const progression = calculerProgression(carte);
    return [
      carte.code_barre,
      carte.types_produits?.nom || '',
      carte.nom_attribue || '',
      carte.date_expedition_max ? new Date(carte.date_expedition_max).toLocaleDateString('fr-FR') : '',
      semaine,
      getStatutLabel(carte),
      progression.validees,
      progression.totales,
      progression.pourcentage,
      new Date(carte.date_creation || carte.created_at).toLocaleDateString('fr-FR')
    ];
  });

  // Convertir en CSV
  const csvContent = [
    headers.join(';'),
    ...rows.map(row => row.map(cell => `"${cell}"`).join(';'))
  ].join('\n');

  // Ajouter le BOM UTF-8 pour Excel
  return '\uFEFF' + csvContent;
};
